import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {Box, Collapse, Grid, Link, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useViewport} from '_customHooks'
import {
    getStoriesVisibleItemsCount,
    isStoriesItemComponent,
    isStoriesLayoutComponent,
    STORIES_VISUAL_LAYOUTS,
    type PageContentStoriesLayoutComponent,
} from '_utils'
import StoryCard from '../story-card'

export interface StorySectionProps {
    section: PageContentStoriesLayoutComponent
    /** Base path of the Stories page, used to build the expanded-section URL. */
    basePath: string
    /** When true every story is rendered in the 3-column grid and the CTA is hidden. */
    isExpanded?: boolean
    /** Renders the section heading at a lower level; used for nested subsections. */
    isSubsection?: boolean
    /** Set to false when the heading is already rendered elsewhere, ex. the mobile sidebar. */
    hasHeading?: boolean
    isFirstSection?: boolean
}

// Grid template per visual layout. Only the root expanded section (not its nested subsections)
// falls back to the flat 3-column grid, regardless of its own visualLayout.
const getGridColumns = (visualLayout?: string, useOwnLayout?: boolean) => {
    if (!useOwnLayout || !visualLayout) return {base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}
    switch (visualLayout) {
        case STORIES_VISUAL_LAYOUTS.TWO_STORIES:
            return {base: 'repeat(2, 1fr)'}
        case STORIES_VISUAL_LAYOUTS.THREE_STORIES:
        case STORIES_VISUAL_LAYOUTS.BANNER_FULL_WITH_THREE:
            return {base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}
        case STORIES_VISUAL_LAYOUTS.BANNER_FULL:
        default:
            return {base: 'repeat(1, 1fr)'}
    }
}

const getAnalyticsCardType = (layout: string | undefined, position: number, isExpanded: boolean, isSubsection: boolean): 'single' | 'double' | 'triple' => {
    if (isExpanded && !isSubsection) {
        return 'triple'
    }

    if (layout === STORIES_VISUAL_LAYOUTS.BANNER_FULL_WITH_THREE) {
        return position === 1 ? 'single' : 'triple'
    }

    if (layout === STORIES_VISUAL_LAYOUTS.BANNER_FULL) {
        return 'single'
    }

    if (layout === STORIES_VISUAL_LAYOUTS.TWO_STORIES) {
        return 'double'
    }

    if (layout === STORIES_VISUAL_LAYOUTS.THREE_STORIES) {
        return 'triple'
    }

    return 'single'
}

/**
 * @name StorySection
 *
 * @component
 *
 * @description
 * Renders a Page Designer `pwaStoriesComponent`: title, subtitle, the stories laid out according
 * to the section `visualLayout`, and the "View all" CTA that opens the expanded section.
 * In the collapsed view only the first N stories are rendered, where N depends on the layout
 * and the viewport; the expanded view renders every story plus the nested subsections.
 *
 * @param {StorySectionProps} props - The properties object.
 * @param {PageContentStoriesLayoutComponent} props.section - The mapped section.
 * @param {string} props.basePath - Base path of the Stories page.
 * @param {boolean} [props.isExpanded] - Whether the section is rendered in its expanded view.
 * @param {boolean} [props.isSubsection] - Whether the section is nested inside another one.
 *
 * @returns {JSX.Element | null} The rendered `StorySection`, or `null` when it holds no story.
 */
const StorySection: React.FC<StorySectionProps> = ({
    section,
    basePath,
    isExpanded = false,
    isSubsection = false,
    hasHeading = true,
    isFirstSection = false,
}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('Stories', {})
    const {isMobile} = useViewport()
    const [isSubsectionExpanded, setIsSubsectionExpanded] = useState(false)

    // The `items` region is generic: split the authored order into story cards and nested sections.
    const stories = (section.items ?? []).filter(isStoriesItemComponent)
    const subsections = (section.items ?? []).filter(isStoriesLayoutComponent)

    if (!stories.length && !subsections.length) return null

    const visibleCount = getStoriesVisibleItemsCount(section.visualLayout, isMobile)
    const visibleItems = isExpanded && !isSubsection ? stories : stories.slice(0, visibleCount)
    // Nested subsections always keep their own visualLayout: only the root section is forced into the
    // flat expanded grid, since a subsection is displayed inline rather than through its own "view all".
    const useOwnLayout = !isExpanded || isSubsection
    // Layout 4 leads with a full-width banner followed by the remaining stories in the grid.
    const hasLeadingBanner = useOwnLayout && section.visualLayout === STORIES_VISUAL_LAYOUTS.BANNER_FULL_WITH_THREE
    // Layout 1 renders its single story as a full-width banner.
    const isBannerLayout = useOwnLayout && section.visualLayout === STORIES_VISUAL_LAYOUTS.BANNER_FULL

    const hasMoreContent = stories.length > visibleItems.length || subsections.length > 0
    const hasRemainingSubsectionStories = isSubsection && stories.length > visibleCount
    const showCta = isSubsection ? hasRemainingSubsectionStories && !isSubsectionExpanded : !isExpanded && hasMoreContent && Boolean(section.sectionId)
    const ctaLabel = section.ctaLabel || formatMessage({id: 'stories.nav.view_all', defaultMessage: 'View all'})
    const renderStoryCard = (story: (typeof stories)[number], index: number) => (
        <StoryCard
            key={story.id ?? `${section.id}-story-${index}`}
            story={story}
            isFullWidth={story.fullWidth || isBannerLayout || (hasLeadingBanner && index === 0)}
            isPriority={isFirstSection && index < (isMobile ? 2 : 3)}
            position={index + 1}
            template={section.visualLayout}
            analyticsCardType={getAnalyticsCardType(section.visualLayout, index + 1, isExpanded, isSubsection)}
        />
    )

    return (
        <Box as="section" sx={isSubsection ? styles.subsection : styles.section} data-testid="story-section">
            {hasHeading && section.title && (
                <Text as={isSubsection ? 'h3' : 'h2'} sx={isSubsection ? styles.subsectionTitle : styles.sectionTitle}>
                    {section.title}
                </Text>
            )}
            {hasHeading && section.subtitle && <Text sx={styles.sectionSubtitle}>{section.subtitle}</Text>}

            <Grid sx={styles.grid} templateColumns={getGridColumns(section.visualLayout, useOwnLayout)}>
                {visibleItems.map(renderStoryCard)}
                {hasRemainingSubsectionStories && (
                    <Box gridColumn="1 / -1">
                        <Collapse in={isSubsectionExpanded} animateOpacity>
                            <Grid sx={styles.grid} templateColumns={getGridColumns(section.visualLayout, useOwnLayout)}>
                                {stories.slice(visibleCount).map((story, index) => renderStoryCard(story, index + visibleCount))}
                            </Grid>
                        </Collapse>
                    </Box>
                )}
            </Grid>

            {showCta &&
                (isSubsection ? (
                    <Link as="button" type="button" onClick={() => setIsSubsectionExpanded(true)} sx={styles.sectionCta} data-testid="story-section-cta">
                        {ctaLabel}
                    </Link>
                ) : (
                    <Link as={RouteLink} to={`${basePath}/${section.sectionId}`} sx={styles.sectionCta} data-testid="story-section-cta">
                        {ctaLabel}
                    </Link>
                ))}

            {isExpanded &&
                subsections.map((subsection, index) => (
                    <StorySection key={subsection.id ?? `${section.id}-subsection-${index}`} section={subsection} basePath={basePath} isExpanded isSubsection />
                ))}
        </Box>
    )
}

export default StorySection
